import { getLoggedinUser } from "./apiCore";
import api from "../utils/api";
import axios from "axios";
import { URLS } from "../constants";
import logger from "../utils/logger";

// Helper function to get base URL
const getBaseUrl = (): string => {
  return (
    process.env.REACT_APP_PROCESS_API_BASE_URL ||
    "https://dev-agent-proxy.jesss.ai"
  );
};

export const agentApi = axios.create({
  baseURL: `${URLS.AGENT_PROXY_URL}`,
  withCredentials: true,
});

// Helper function to build common headers
const buildApiHeaders = (
  additionalHeaders: Record<string, string> = {},
): Record<string, string> => {
  const user = getLoggedinUser();
  const companyId = user?.companyID || null;

  return {
    ...additionalHeaders,
    ...(companyId && { "X-Company-ID": companyId }),
  };
};

// Helper function to build axios config
const buildAxiosConfig = (
  additionalHeaders: Record<string, string> = {},
): { headers: Record<string, string>; withCredentials: boolean } => {
  return {
    headers: buildApiHeaders(additionalHeaders),
    withCredentials: true,
  };
};

// Agent connection status response interface
export interface AgentConnectionStatus {
  companyId: string;
  isAgentConnected: boolean;
  timestamp: number;
}

// Fetch agent connection status
export const fetchAgentConnectionStatus =
  async (): Promise<AgentConnectionStatus> => {
    // Check for forced offline mode via environment variable
    if (process.env.REACT_APP_FORCE_AGENT_OFFLINE === "true") {
      const user = getLoggedinUser();
      const forcedStatus = {
        companyId: user?.companyID || "test-company",
        isAgentConnected: false,
        timestamp: Date.now(),
      };
      return forcedStatus;
    }

    try {
      const url = `${getBaseUrl()}/api/agent/connection-status`;
      const config = buildAxiosConfig();

      // Add timeout to prevent blocking page load if agent-proxy is slow/unavailable
      const configWithTimeout = {
        ...config,
        timeout: 2000, // 2 seconds timeout
      };

      // Due to global axios interceptor in apiCore.ts, response is already unwrapped (response.data)
      const { data } = await api.get<AgentConnectionStatus>(
        url,
        configWithTimeout,
      );

      // Validate response data (data is already the unwrapped response.data)
      if (!data) {
        throw new Error("API returned empty response");
      }

      // Validate response structure
      if (typeof data !== "object" || !("isAgentConnected" in data)) {
        throw new Error("API returned invalid data structure");
      }

      return data;
    } catch (error) {
      logger.debug("Agent-proxy unavailable ", error);

      // Return default offline status if error
      const user = getLoggedinUser();
      const errorStatus = {
        companyId: user?.companyID || "unknown",
        isAgentConnected: false,
        timestamp: Date.now(),
      };
      return errorStatus;
    }
  };
